/**
 * compat.ts — backwards-compat shim for the admin-portal.
 *
 * The generated client uses HTTP-method + path naming (e.g. getTenants, postTenants).
 * The portal was built against friendly names (e.g. listTenants, createTenant).
 *
 * This file:
 *   1. Re-exports generated functions under their old friendly names.
 *   2. Provides thin wrappers that adapt old calling conventions to new signatures.
 *   3. Provides adminBffFetch-backed stubs for endpoints not in the generated spec.
 *   4. Exports DTO interfaces that the portal references.
 *
 * Do NOT modify generated files — only this file and src/index.ts.
 */

import type {
  BodyBody,
  getAgentsIdResponse,
  getAgentsResponse,
  PostAgentsBody,
  PostChatSessionsBody,
  PostChatSessionsIdMessagesBody,
  PostDeploymentsBody,
  PostTenantsSlugModelsAliasesBody,
  PutAgentsIdOverridesBody,
} from './generated/index.js';
import {
  deleteAgentsIdOverrides,
  deleteApplicationsAppIDRolesRoleIDUsersUserID,
  getApplications,
  getApplicationsAppIDRoles,
  getTenants,
  getTenantsSlugUsers,
  patchAgentsId,
  patchDeploymentsId,
  postAgents,
  postApplicationsAppIDRolesRoleIDUsersUserID,
  postApplicationsSlugTenants,
  postAuthLogout,
  postChatSessions,
  postChatSessionsIdMessages,
  postDeployments,
  postTenantsSlugModelsAliases,
  putAgentsIdOverrides,
} from './generated/index.js';
import { adminBffFetch } from './mutator.js';

// ─── Response type aliases ─────────────────────────────────────────────────────

export type listAgentsResponse = getAgentsResponse;
export type getAgentResponse = getAgentsIdResponse;

// ─── Re-export generated functions under friendly names ────────────────────────

export {
  deleteAgentsId as deleteAgent,
  deleteAgentsIdOverrides,
  deleteAgentsIdToolsToolId as unassignTool,
  deleteApplicationsAppIDPermissionsPermID as deletePermission,
  deleteApplicationsAppIDRolesRoleID as deleteRole,
  deleteApplicationsAppIDRolesRoleIDPermissionsPermID as removePermissionFromRole,
  deleteApplicationsSlug as deleteApplication,
  deleteApplicationsSlugCredentialsCredID as deleteApplicationCredential,
  deleteApplicationsSlugEnvironmentsEnvSlug as deleteEnvironment,
  deleteApplicationsSlugEnvironmentsEnvSlug as deleteApplicationEnvironment,
  deleteApplicationsSlugTenantsTenantID as unbindTenantApplication,
  deleteChatSessionsId as deleteChatSession,
  deleteChatSessionsIdMessages as deleteChatMessages,
  deleteDeploymentsId as deleteDeployment,
  deleteTenantsId as deleteTenant,
  deleteTenantsSlugConfigKey as deleteConfig,
  deleteTenantsSlugCredentialsId as deleteCredential,
  deleteTenantsSlugModelsAliasesId as deleteModelAlias,
  deleteTenantsSlugStorage as deleteStorage,
  deleteTenantsSlugUsersId as deleteUser,
  deleteToolsId as deleteTool,
  // Agents (generated from Go BFF proxy annotations)
  getAgents as listAgents,
  getAgentsId as getAgent,
  getAgentsIdOverrides as listAgentOverrides,
  getAgentsIdResolve as resolveAgent,
  getAgentsIdTools as listAgentTools,
  // RBAC — permissions
  getApplicationsAppIDPermissions as listPermissions,
  // Applications
  getApplicationsSlug as getApplication,
  getApplicationsSlugCredentials as listApplicationCredentials,
  getApplicationsSlugEnvironments as listApplicationEnvironments,
  // Chat (generated from Go BFF proxy annotations)
  getChatSessions as listChatSessions,
  getChatSessionsId as getChatSession,
  getChatSessionsIdMessages as listChatMessages,
  getConfigApps as getAppRegistry,
  getConfigBootstrap as getBootstrapConfig,
  // Deployments (generated from Go BFF proxy annotations)
  getDeployments as listDeployments,
  getDeploymentsId as getDeployment,
  getGetAgentsIdUrl as getGetAgentUrl,
  // URL getters (generated)
  getGetAgentsUrl as getListAgentsUrl,
  getGetDeploymentsIdUrl as getGetDeploymentUrl,
  getGetDeploymentsUrl as getListDeploymentsUrl,
  // Models (generated from Go BFF proxy annotations)
  getModelsCatalog as listModels,
  // Tenants
  getTenantsId as getTenant,
  // Tenant config
  getTenantsSlugConfig as getConfig,
  getTenantsSlugConfigFeatureFlags as getFeatureFlags,
  getTenantsSlugConfigNav as getNavConfig,
  // Tenant credentials
  getTenantsSlugCredentials as listCredentials,
  getTenantsSlugModelsAliases as listModelAliases,
  getTenantsSlugStorage as listStorage,
  getTenantsSlugUsersIdProfile as getProfile,
  // Entitlements
  getTenantsSlugUsersUserIdEntitlements as getUserEntitlements,
  // Tools
  getTools as listTools,
  patchApplicationsAppIDRolesRoleID as updateRole,
  patchApplicationsSlug as updateApplication,
  patchApplicationsSlugEnvironmentsEnvSlug as updateEnvironment,
  patchApplicationsSlugEnvironmentsEnvSlug as updateApplicationEnvironment,
  patchTenantsId as updateTenant,
  patchTenantsSlugConfigKey as updateConfig,
  patchTenantsSlugCredentialsId as updateCredential,
  patchTenantsSlugUsersId as updateUser,
  patchTenantsSlugUsersIdProfile as updateProfile,
  patchToolsId as updateTool,
  postAgentsIdTools as assignTool,
  postApplications as createApplication,
  postApplicationsAppIDPermissions as createPermission,
  // RBAC — roles (no-user-assignment variants)
  postApplicationsAppIDRoles as createRole,
  postApplicationsAppIDRolesBulk as bulkRoleOps,
  postApplicationsAppIDRolesRoleIDPermissionsPermID as assignPermissionToRole,
  postApplicationsSlugCredentials as createApplicationCredential,
  postApplicationsSlugEnvironments as createEnvironment,
  postApplicationsSlugEnvironments as createApplicationEnvironment,
  // Auth
  postAuthLogin as loginAuth,
  postAuthOtpSend as sendOtp,
  postAuthOtpVerify as verifyOtp,
  postAuthTokenRefresh as refreshToken,
  postChatSessionsIdClose as closeChatSession,
  postTenants as createTenant,
  postTenantsIdReactivate as reactivateTenant,
  postTenantsIdRestore as restoreTenant,
  postTenantsIdSuspend as suspendTenant,
  postTenantsSlugConfig as createConfig,
  postTenantsSlugConfigBootstrap as bootstrapConfig,
  postTenantsSlugConfigCacheInvalidate as invalidateCache,
  postTenantsSlugCredentials as createCredential,
  postTenantsSlugCredentialsIdRotate as rotateCredential,
  postTenantsSlugCredentialsIdValidate as validateCredential,
  // Storage
  postTenantsSlugStorageSignedUrl as getSignedUrl,
  // User management (slug-scoped calls)
  postTenantsSlugUsers as createUser,
  postTenantsSlugUsersIdPasswordReset as resetUserPassword,
  postTenantsSlugUsersIdReactivate as reactivateUser,
  postTenantsSlugUsersIdSuspend as suspendUser,
  postTenantsSlugUsersInvite as inviteUser,
  postTools as createTool,
  postToolsIdValidate as validateTool,
  putTenantsSlugConfigFeatureFlags as setFeatureFlags,
  putTenantsSlugConfigNav as setNavConfig,
  putTenantsSlugUsersUserIdEntitlements as setUserEntitlements,
} from './generated/index.js';

// ─── Pagination + signature wrappers ──────────────────────────────────────────

/** listTenants — portal calls with optional pagination params */
export const listTenants = (
  _params?: { limit?: number; offset?: number } | Record<string, unknown>,
) => getTenants();

/** listApplications — portal calls with optional pagination params */
export const listApplications = (
  _params?: { limit?: number; offset?: number } | Record<string, unknown>,
) => getApplications();

/** listUsers — portal calls with (slug, { limit, offset, status? }) */
export const listUsers = (
  slug: string,
  _params?: { limit?: number; offset?: number; status?: string } | Record<string, unknown>,
) => getTenantsSlugUsers(slug);

/** listRoles — when called without an appID, returns all roles across all applications */
export const listRoles = (
  appIDOrParams?: string | { limit?: number; offset?: number } | Record<string, unknown>,
  _params?: Record<string, unknown>,
) => {
  const appID = typeof appIDOrParams === 'string' ? appIDOrParams : '';
  if (!appID) return adminBffFetch<{ data: { data: unknown[] }; status: number }>('/api/v1/roles');
  return getApplicationsAppIDRoles(appID);
};

/** assignRole — portal calls with (tenantSlug, { roleId, userId, applicationId }) */
export const assignRole = (tenantSlug: string, dto: AssignRoleDto) =>
  postApplicationsAppIDRolesRoleIDUsersUserID(
    dto.applicationId ?? tenantSlug,
    dto.roleId,
    dto.userId,
  );

/** revokeRole — portal calls with (tenantSlug, { userId, roleId, applicationId }) */
export const revokeRole = (
  tenantSlug: string,
  dto: { userId: string; roleId: string; applicationId?: string },
) =>
  deleteApplicationsAppIDRolesRoleIDUsersUserID(
    dto.applicationId ?? tenantSlug,
    dto.roleId,
    dto.userId,
  );

/** bindTenantApplication — portal calls with (tenantSlug, appId) */
export const bindTenantApplication = (slug: string, appId: string) =>
  postApplicationsSlugTenants(appId, { body: JSON.stringify({ tenant_id: slug }) });

export const logout = () => postAuthLogout({});

// ─── Chat thin wrappers — body params require cast from unknown ────────────────

export const createChatSession = (body: unknown) => postChatSessions(body as PostChatSessionsBody);
export const sendChatMessage = (id: string, body: unknown) =>
  postChatSessionsIdMessages(id, body as PostChatSessionsIdMessagesBody);

// ─── Agent thin wrappers ───────────────────────────────────────────────────────

export const createAgent = (body: unknown) => postAgents(body as PostAgentsBody);
export const updateAgent = (id: string, body: unknown) => patchAgentsId(id, body as BodyBody);
// baseAgentId is the UUID of the base (APPLICATION/PUBLIC-scoped) agent.
// The backend upserts the TENANT-scoped override for the calling tenant (from JWT).
export const createAgentOverride = (baseAgentId: string, body: unknown) =>
  putAgentsIdOverrides(baseAgentId, body as PutAgentsIdOverridesBody);
export const updateAgentOverride = (baseAgentId: string, _id: string, body: unknown) =>
  putAgentsIdOverrides(baseAgentId, body as PutAgentsIdOverridesBody);
export const deleteAgentOverride = (baseAgentId: string, _id: string) =>
  deleteAgentsIdOverrides(baseAgentId);

// ─── Deployment thin wrappers ─────────────────────────────────────────────────

export const createDeployment = (body: unknown) => postDeployments(body as PostDeploymentsBody);
export const updateDeployment = (id: string, body: unknown) =>
  patchDeploymentsId(id, body as BodyBody);

// ─── Model thin wrappers ──────────────────────────────────────────────────────

export const createModelAlias = (slug: string, body: unknown) =>
  postTenantsSlugModelsAliases(slug, body as PostTenantsSlugModelsAliasesBody);

// ─── Stubs — proxy / missing endpoints not yet in the generated spec ───────────

// Admin metadata
export const updateTenantAdminMetadata = (id: string, body: UpdateAdminMetadataDto) =>
  adminBffFetch(`/api/v1/tenants/${id}/admin-metadata`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

// Tenant-scoped application list (filter view)
export const listTenantApplications = (tenantSlug: string) =>
  adminBffFetch(`/api/v1/applications?tenantSlug=${encodeURIComponent(tenantSlug)}`, {
    method: 'GET',
  });

// User approval / rejection
export const approveUser = (slug: string, userId: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/users/${userId}/reactivate`, { method: 'POST' });
export const rejectUser = (slug: string, userId: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/users/${userId}/suspend`, { method: 'POST' });
export const listUserRoles = (slug: string, userId: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/users/${userId}/roles`, { method: 'GET' });

// Storage config
export const getTenantStorage = (slug: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/storage-config`, { method: 'GET' });
export const deleteTenantStorage = (slug: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/storage-config`, { method: 'DELETE' });
export const setTenantStorage = (slug: string, body: unknown) =>
  adminBffFetch(`/api/v1/tenants/${slug}/storage-config`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// Integrations — setTenantIntegration(slug, provider, dto)
export const listTenantIntegrations = (slug: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/integrations`, { method: 'GET' });
export const setTenantIntegration = (slug: string, provider: string, body: unknown) =>
  adminBffFetch(`/api/v1/tenants/${slug}/integrations/${encodeURIComponent(provider)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
export const deleteTenantIntegration = (slug: string, provider: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/integrations/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  });

// IDP — all take (slug, idpId, ...) to allow tenant-scoped routing
export const listIdpConfigs = (slug: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp`, { method: 'GET' });
export const createIdpConfig = (slug: string, body: unknown) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp`, { method: 'POST', body: JSON.stringify(body) });
export const updateIdpConfig = (slug: string, id: string, body: unknown) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
export const deleteIdpConfig = (slug: string, id: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${id}`, { method: 'DELETE' });
export const testIdpConfig = (slug: string, id: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${id}/test`, { method: 'POST' });
export const testGraphCredentials = (slug: string, id: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${id}/test-graph`, { method: 'POST' });

// IdP bindings
export const listAppIdpBindings = (slug: string, idpId: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${idpId}/bindings`, { method: 'GET' });
export const createAppIdpBinding = (slug: string, idpId: string, body: unknown) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${idpId}/bindings`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
export const updateAppIdpBinding = (
  slug: string,
  idpId: string,
  bindingId: string,
  body: unknown,
) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${idpId}/bindings/${bindingId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
export const deleteAppIdpBinding = (slug: string, idpId: string, bindingId: string) =>
  adminBffFetch(`/api/v1/tenants/${slug}/idp/${idpId}/bindings/${bindingId}`, { method: 'DELETE' });

// Directory users
export const searchDirectoryUsers = (
  slug: string,
  idpId: string,
  params?: { q?: string; top?: number },
) => {
  const qs = params
    ? `?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(params)
            .filter(([, v]) => v != null)
            .map(([k, v]) => [k, String(v)]),
        ),
      ).toString()}`
    : '';
  return adminBffFetch(`/api/v1/tenants/${slug}/idp/${idpId}/directory/users${qs}`, {
    method: 'GET',
  });
};

// Factory (separate service — stubs only)
type FactoryResp = { status: number; data: { data: unknown }; headers: Headers };
export const factoryGetAdo = () =>
  adminBffFetch<FactoryResp>('/api/v1/factory/ado', { method: 'GET' });
export const factoryGetDispatch = () =>
  adminBffFetch<FactoryResp>('/api/v1/factory/dispatch', { method: 'GET' });
export const factoryGetModels = () =>
  adminBffFetch<FactoryResp>('/api/v1/factory/models', { method: 'GET' });
export const factoryUpdateModels = (body: unknown) =>
  adminBffFetch<FactoryResp>('/api/v1/factory/models', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
export const factoryGetTeam = () =>
  adminBffFetch<FactoryResp>('/api/v1/factory/team', { method: 'GET' });
export const factoryListQueues = () =>
  adminBffFetch<FactoryResp>('/api/v1/factory/queues', { method: 'GET' });
export const factoryTriggerQueue = (name: string) =>
  adminBffFetch<FactoryResp>(`/api/v1/factory/queues/${name}/trigger`, { method: 'POST' });
export const factoryUpdateQueue = (name: string, body: unknown) =>
  adminBffFetch<FactoryResp>(`/api/v1/factory/queues/${name}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
export const factoryListAgentRoutes = () =>
  adminBffFetch<FactoryResp>('/api/v1/factory/agent-routes', { method: 'GET' });
export const factoryUpsertAgentRoute = (id: string, body: unknown) =>
  adminBffFetch<FactoryResp>(`/api/v1/factory/agent-routes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// API Keys — endpoint removed from generated spec; stub via adminBffFetch
export const listApiKeys = (slug: string) =>
  adminBffFetch<{ data: { data: AppApiKeyDto[] }; status: number }>(
    `/api/v1/applications/${encodeURIComponent(slug)}/api-keys`,
    { method: 'GET' },
  );
export const createApiKey = (slug: string, body: unknown) =>
  adminBffFetch<{ data: AppApiKeyDto; status: number }>(
    `/api/v1/applications/${encodeURIComponent(slug)}/api-keys`,
    { method: 'POST', body: JSON.stringify(body) },
  );
export const deleteApiKey = (slug: string, id: string) =>
  adminBffFetch(
    `/api/v1/applications/${encodeURIComponent(slug)}/api-keys/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
    },
  );

// Auth config (admin) — per-tenant token TTLs and auth settings
export const getAdminAuthConfig = (slug: string) =>
  adminBffFetch(`/api/v1/tenants/${encodeURIComponent(slug)}/auth-config`, { method: 'GET' });
export const updateAdminAuthConfig = (slug: string, body: Partial<AuthConfigDto>) =>
  adminBffFetch(`/api/v1/tenants/${encodeURIComponent(slug)}/auth-config`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

// Current user profile
export const getCurrentUser = (tenantSlug: string) =>
  adminBffFetch(`/api/v1/tenants/${tenantSlug}/users/me/profile`, { method: 'GET' });
export const updateCurrentUser = (tenantSlug: string, body: unknown) =>
  adminBffFetch(`/api/v1/tenants/${tenantSlug}/users/me/profile`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

// Public auth config — called by login-portal and code-factory TenantBootstrapProvider
// Pass appSlug to include application branding (logoUrl, displayName) in the response.
export const getAuthConfig = (tenantSlug: string, appSlug?: string) => {
  const qs = appSlug ? `?app=${encodeURIComponent(appSlug)}` : '';
  return adminBffFetch(`/api/v1/auth/config/${encodeURIComponent(tenantSlug)}${qs}`, {
    method: 'GET',
  });
};

// Email/password login — used by login-portal and code-factory
export const login = (body: { email: string; password: string }, _opts?: { tenantSlug?: string }) =>
  adminBffFetch('/api/v1/auth/login', { method: 'POST', body: JSON.stringify(body) });

// MSAL token exchange — code-factory AuthCallbackView exchanges a Graph token for a BFF JWT
export const msalLogin = (body: { graphToken: string }, _opts?: { tenantSlug?: string }) =>
  adminBffFetch('/api/v1/auth/msal/token', { method: 'POST', body: JSON.stringify(body) });

// User self-registration
export const register = (
  tenantSlug: string,
  body: { email: string; password: string; firstName?: string; lastName?: string },
) =>
  adminBffFetch(`/api/v1/tenants/${encodeURIComponent(tenantSlug)}/users/register`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Accept an email invitation and set a password
export const acceptInvite = (tenantSlug: string, body: { token: string; password: string }) =>
  adminBffFetch(`/api/v1/tenants/${encodeURIComponent(tenantSlug)}/users/invite/accept`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Request a password-reset email
export const resetPasswordRequest = (body: { email: string; tenantSlug: string }) =>
  adminBffFetch('/api/v1/auth/password-reset', { method: 'POST', body: JSON.stringify(body) });

// Confirm a password reset using the token from the email
export const resetPasswordConfirm = (body: {
  token: string;
  tenantSlug: string;
  newPassword: string;
}) =>
  adminBffFetch('/api/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify(body),
  });

// SSO callback — proxy to the BFF auth/sso/callback route
export const handleSsoCallback = (
  tenantSlug: string,
  _provider: 'azure' | 'google',
  body: { code: string; state: string; redirect_uri?: string; application_slug?: string },
) =>
  adminBffFetch(`/api/v1/auth/sso/callback`, {
    method: 'POST',
    body: JSON.stringify({ ...body, tenantSlug }),
  });

// SSO redirect URL — login-portal LoginPage initiates SSO by fetching the provider redirect URL
export const getSsoRedirectUrl = (
  tenantSlug: string,
  provider: 'azure' | 'google',
  params?: { redirect_uri?: string; application_slug?: string },
) => {
  const qs = new URLSearchParams({
    tenant_slug: tenantSlug,
    ...(params as Record<string, string> | undefined),
  }).toString();
  return adminBffFetch(`/api/v1/auth/sso/${encodeURIComponent(provider)}/redirect?${qs}`, {
    method: 'GET',
  });
};

// ─── DTO interfaces ────────────────────────────────────────────────────────────

// Tenant environment enum
export const TenantDtoEnvironment = {
  prod: 'prod',
  qa: 'qa',
  dev: 'dev',
} as const;
export type TenantDtoEnvironment = (typeof TenantDtoEnvironment)[keyof typeof TenantDtoEnvironment];

// Public auth config returned by GET /auth/config/{tenantSlug}[?app=<slug>]
export interface TenantAuthConfigPublicDto {
  tenantSlug: string;
  tenantName?: string;
  authMode: 'password_only' | 'sso_only' | 'mixed';
  ssoProviders: Array<{ provider: string; displayName: string }>;
  idpConfigs: Array<{
    provider: string;
    displayName: string;
    enabled: boolean;
    allowedDomains: string[];
  }>;
  allowSelfRegistration: boolean;
  loginPortalURL?: string;
  // Application branding — only present when ?app=<slug> is passed
  applicationSlug?: string;
  applicationName?: string;
  applicationLogoUrl?: string;
}

// Tenant auth config — per-tenant auth settings managed via /tenants/{slug}/auth-config
export interface AuthConfigDto {
  tenantSlug?: string;
  passwordEnabled?: boolean;
  jitProvisioning?: boolean;
  allowSelfRegistration?: boolean;
  requireApproval?: boolean;
  inviteTtlHours?: number;
  accessTokenTtlSeconds?: number;
  refreshTokenTtlSeconds?: number;
  sessionIdleTimeoutMinutes?: number;
  maxLoginAttempts?: number;
  mfaRequired?: boolean;
}

export interface TenantDto {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  status: 'active' | 'suspended' | 'deleted';
  contactEmail?: string;
  environment?: 'prod' | 'qa' | 'dev';
  logoUrl?: string | null;
  avatarIcon?: string | null;
  avatarBgColor?: string | null;
  systemProtected?: boolean;
  deletedAt?: string | null;
  adminMetadata?: Record<string, unknown>;
  tenantMetadata?: Record<string, unknown>;
  authConfig?: AuthConfigDto;
  createdAt: string;
  updatedAt: string;
  /** App settings overrides keyed by appSlug → section → values. Present only on GET /tenants/{id}. */
  settings?: Record<string, Record<string, Record<string, unknown>>>;
}
export type TenantResponseDto = TenantDto;

export interface CreateTenantDto {
  slug: string;
  name?: string;
  displayName?: string;
  environment?: 'prod' | 'qa' | 'dev';
}

export interface UpdateTenantDto {
  name?: string;
  displayName?: string;
  contactEmail?: string;
  logoUrl?: string | null;
  avatarIcon?: string | null;
  avatarBgColor?: string | null;
  environment?: 'prod' | 'qa' | 'dev';
  status?: 'active' | 'suspended' | 'inactive';
  authConfig?: AuthConfigDto;
}

// Application types
export interface ApplicationEnvironmentDto {
  id: string;
  slug: string;
  name: string;
  applicationId: string;
  description?: string;
  url?: string;
  notes?: string;
  auth?: EnvironmentAuthResponseDto;
  variables?: Record<string, string>;
  storage?: { type?: string; bucket?: string; container?: string; pathPrefix?: string };
  createdAt: string;
  updatedAt: string;
}

export interface EnvironmentAuthDto {
  tenantSlug?: string;
  applicationSlug?: string;
  environmentSlug?: string;
  provider?: string;
  clientId?: string;
  clientSecret?: string;
  tenantId?: string;
  audience?: string;
  redirectUri?: string;
}

export interface EnvironmentAuthResponseDto {
  accessToken?: string;
  expiresIn?: number;
  provider?: string;
  clientId?: string;
  tenantId?: string;
  audience?: string;
  redirectUri?: string;
  hasSecret?: boolean;
}

export interface ApplicationDto {
  id: string;
  slug: string;
  name: string;
  displayName: string;
  type?: string;
  description?: string;
  repositoryUrl?: string | null;
  logoUrl?: string | null;
  status?: string;
  tenantCount?: number;
  applicationCount?: number;
  environments?: ApplicationEnvironmentDto[];
  configuration?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateApplicationDto {
  name?: string;
  displayName?: string;
  type?: string;
  description?: string;
  repositoryUrl?: string | null;
  logoUrl?: string | null;
  status?: string;
  configuration?: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

export interface AppApiKeyDto {
  id: string;
  name: string;
  prefix: string;
  key?: string;
  created_at: string;
}

export interface RbacRoleDto {
  id: string;
  application_id: string;
  name: string;
  description: string;
  protected: boolean;
  created_at: string;
}

export interface RbacPermissionDto {
  id: string;
  application_id: string;
  resource: string;
  action: string;
  created_at: string;
}

export interface CreateApplicationDto {
  slug: string;
  name?: string;
  displayName?: string;
  type?: string;
  description?: string;
  repositoryUrl?: string;
}

export interface CreateEnvironmentDto {
  slug?: string;
  name: string;
  description?: string;
  url?: string;
  auth?: EnvironmentAuthDto;
  variables?: Record<string, string>;
  storage?: { container?: string; pathPrefix?: string };
  notes?: string;
}

export interface AppCredentialDto {
  id: string;
  application_id: string;
  key: string;
  created_at: string;
}

export interface BoundTenantDto {
  tenantId: string;
  slug: string;
  displayName: string;
  boundAt: string;
}

export interface UpdateEnvironmentDto {
  name?: string;
  auth?: EnvironmentAuthDto;
  description?: string;
  url?: string;
  notes?: string;
  variables?: Record<string, string>;
  storage?: { type?: string; bucket?: string; container?: string; pathPrefix?: string };
}

export interface TenantApplicationDto {
  id: string;
  tenantId: string;
  applicationId: string;
  slug?: string;
  displayName?: string;
  type?: string;
  boundAt?: string;
  createdAt: string;
}

export interface TenantApplicationsResponseDto {
  items: TenantApplicationDto[];
  total: number;
}

export interface PaginatedApplicationsResponseDto {
  items: ApplicationDto[];
  total: number;
}

// User types
export interface UserDto {
  id: string;
  email: string;
  status: 'active' | 'suspended' | 'pending_invite';
  roles: string[];
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  avatarUrl?: string | null;
  updatedAt?: string;
  lastLoginAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: string | null;
  inviteTokenExpiresAt?: string | null;
  customFields?: Record<string, unknown>;
  createdAt: string;
}

export interface CreateUserDto {
  email: string;
  roles?: string[];
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  phoneNumber?: string;
  roles?: string[];
}

export interface InviteUserDto {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface PaginatedUsersResponseDto {
  items: UserDto[];
  total: number;
}

export interface AssignRoleDto {
  roleId: string;
  userId: string;
  applicationId?: string;
}

export interface UserRoleAssignmentDto {
  id?: string;
  userId: string;
  roleId: string;
  roleName: string;
  applicationId?: string;
  assignedAt?: string;
  assignedBy?: string;
}

export interface DirectoryUserDto {
  id?: string;
  email: string;
  displayName: string;
  externalId: string;
  givenName?: string;
  surname?: string;
  userPrincipalName?: string;
}

// Credential types
export interface CredentialDto {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
}

// IDP types
export interface IdpConfigDto {
  id: string;
  tenantSlug: string;
  provider: string;
  displayName: string;
  enabled: boolean;
  config?: Record<string, unknown>;
  graphClientId?: string;
  allowedDomains?: string[];
  isActive?: boolean;
  createdAt: string;
}

export interface CreateIdpConfigDto {
  provider: string;
  displayName: string;
  config: Record<string, unknown>;
  graphClientId?: string;
  graphClientSecret?: string;
}

export interface UpdateIdpConfigDto {
  displayName?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
  graphClientId?: string;
  graphClientSecret?: string;
}

export interface AppIdpBindingDto {
  id: string;
  tenantSlug: string;
  idpConfigId: string;
  applicationSlug: string;
  applicationId?: string;
  applicationName?: string;
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
  scopes?: string[];
  isActive?: boolean;
}

export interface CreateAppIdpBindingDto {
  idpConfigId?: string;
  applicationSlug?: string;
  applicationId?: string;
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
  scopes?: string[];
  isActive?: boolean;
}

export interface UpdateAppIdpBindingDto {
  enabled?: boolean;
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
  scopes?: string[];
  isActive?: boolean;
}

export interface IdpConfigTestResponseDto {
  success: boolean;
  message?: string;
  reachable?: boolean;
  error?: string;
  discoveryUrl?: string;
}

// Integration types
export type IntegrationProvider =
  | 'openai'
  | 'azure-openai'
  | 'anthropic'
  | 'litellm'
  | 'jira'
  | 'confluence'
  | 'ado'
  | 'github';

export interface TenantIntegrationDto {
  provider: string;
  endpoint?: string;
  baseUrl?: string;
  url?: string;
  orgUrl?: string;
  orgId?: string;
  deploymentName?: string;
  apiVersion?: string;
  defaultModel?: string;
  userEmail?: string;
  defaultProject?: string;
  org?: string;
  teamId?: string;
  virtualKeyHint?: string;
  configured?: boolean;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface TenantIntegrationsDto {
  items: TenantIntegrationDto[];
}

export interface SetTenantIntegrationDto {
  provider?: string;
  apiKey?: string;
  orgId?: string;
  defaultModel?: string;
  endpoint?: string;
  deploymentName?: string;
  apiVersion?: string;
  baseUrl?: string;
  url?: string;
  userEmail?: string;
  token?: string;
  orgUrl?: string;
  org?: string;
  defaultProject?: string;
  [key: string]: unknown;
}

// Storage types
export interface TenantStorageDto {
  provider?: string;
  bucket?: string;
  accountName?: string;
  accountKey?: string;
  connectionString?: string;
  region?: string;
  projectId?: string;
  defaultContainer?: string;
  baseUrl?: string;
  prefix?: string;
  hasCredential?: boolean;
  updatedAt?: string;
}

export interface SetTenantStorageDto {
  provider: string;
  bucket?: string;
  accountName?: string;
  accountKey?: string;
  connectionString?: string;
  region?: string;
  projectId?: string;
  defaultContainer?: string;
  baseUrl?: string;
  prefix?: string;
}

// RBAC types
export interface RoleDto {
  id: string;
  name: string;
  applicationId: string;
  description?: string;
  createdAt: string;
}

export interface UpdateAdminMetadataDto {
  notes?: string;
  tags?: string[];
  adminMetadata?: Record<string, unknown>;
}
